import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Megaphone, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { AdminTabs } from "../../components/admin/AdminTabs";
import { useConfirm } from "../../components/ConfirmProvider";
import { getPublicSettings, setPublicSettings } from "../../lib/settings";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const input = "w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand bg-white";
const empty = { title: "", subtitle: "", image_url: "", link_url: "", placement: "home", price: 0, active: true };

export default function AdminAds() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [adsense, setAdsense] = useState({ adsense_enabled: false, adsense_client: "", adsense_slot: "" });
  const [savingAds, setSavingAds] = useState(false);

  const load = () => api.get("/admin/ads").then(({ data }) => setAds(data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    getPublicSettings().then((s) => setAdsense({
      adsense_enabled: !!s.adsense_enabled,
      adsense_client: s.adsense_client || "",
      adsense_slot: s.adsense_slot || "",
    }));
  }, []);

  const saveAdsense = async () => {
    setSavingAds(true);
    const ok = await setPublicSettings({
      adsense_enabled: adsense.adsense_enabled,
      adsense_client: adsense.adsense_client.trim(),
      adsense_slot: adsense.adsense_slot.trim(),
    });
    toast.success(ok ? t("admin.ads.adsenseSaved") : t("admin.ads.adsenseSavedLocal"));
    setSavingAds(false);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/ads", { ...form, price: Number(form.price) || 0 });
      toast.success(t("admin.ads.saved"));
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(t("common.error")); }
    finally { setSaving(false); }
  };

  const remove = async (ad) => {
    if (!(await confirm({ description: `${t("confirm.deleteDesc")}` }))) return;
    try { await api.delete(`/admin/ads/${ad.id}`); toast.success(t("admin.ads.deleted")); setAds((prev) => prev.filter((x) => x.id !== ad.id)); }
    catch (err) { toast.error(t("common.error")); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-6">{t("admin.ads.title")}</h1>
      <AdminTabs />

      <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-6 mb-6" data-testid="adsense-card">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center"><DollarSign className="w-5 h-5 text-brand" /></div>
          <h2 className="font-heading text-xl font-bold text-stone-text">{t("admin.ads.adsenseTitle")}</h2>
        </div>
        <p className="text-sm text-stone-muted mb-4">{t("admin.ads.adsenseDesc")}</p>
        <label className="flex items-center gap-3 mb-4 cursor-pointer">
          <input data-testid="adsense-enable" type="checkbox" checked={adsense.adsense_enabled} onChange={(e) => setAdsense((a) => ({ ...a, adsense_enabled: e.target.checked }))} className="w-5 h-5 accent-[#D95A41]" />
          <span className="font-medium text-stone-text">{t("admin.ads.adsenseEnable")}</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-stone-sub mb-1">{t("admin.ads.adsenseClient")}</label>
            <input data-testid="adsense-client" className={input} placeholder="ca-pub-1234567890123456" value={adsense.adsense_client} onChange={(e) => setAdsense((a) => ({ ...a, adsense_client: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-sub mb-1">{t("admin.ads.adsenseSlot")}</label>
            <input data-testid="adsense-slot" className={input} placeholder="1234567890" value={adsense.adsense_slot} onChange={(e) => setAdsense((a) => ({ ...a, adsense_slot: e.target.value }))} />
          </div>
        </div>
        <p className="text-xs text-stone-muted mb-4">{t("admin.ads.adsenseHint")}</p>
        <button data-testid="adsense-save" onClick={saveAdsense} disabled={savingAds} className="px-5 py-2.5 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover disabled:opacity-60">
          {savingAds ? "..." : t("admin.ads.adsenseSave")}
        </button>
      </div>

      <button data-testid="ad-add-btn" onClick={() => { setForm(empty); setOpen(true); }} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors mb-6">
        <Plus className="w-4 h-4" /> {t("admin.ads.add")}
      </button>

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : ads.length === 0 ? (
        <div className="py-12 text-center text-stone-muted" data-testid="ads-empty">{t("admin.ads.empty")}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ads-list">
          {ads.map((ad) => (
            <div key={ad.id} className="bg-white rounded-2xl border border-stone-border shadow-sm overflow-hidden" data-testid="ad-item">
              {ad.image_url ? <img src={ad.image_url} alt={ad.title} className="w-full h-36 object-cover" /> : <div className="w-full h-36 bg-stone-bg flex items-center justify-center"><Megaphone className="w-8 h-8 text-stone-muted" /></div>}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-stone-text truncate">{ad.title}</h3>
                    <p className="text-sm text-stone-muted truncate">{ad.subtitle}</p>
                  </div>
                  <button data-testid="ad-delete-btn" onClick={() => remove(ad)} className="p-2 rounded-lg hover:bg-red-50 text-stone-muted hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center gap-2 mt-2 text-xs text-stone-muted">
                  <span className="px-2 py-0.5 rounded-full bg-stone-bg">{ad.placement}</span>
                  <span>${ad.price}</span>
                  <span>· {ad.impressions || 0} {t("admin.ads.impressions")}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ad-modal">
          <DialogHeader><DialogTitle className="font-heading">{t("admin.ads.add")}</DialogTitle><DialogDescription>{t("admin.ads.title")}</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <input data-testid="ad-title" required placeholder={t("admin.ads.adTitle")} className={input} value={form.title} onChange={set("title")} />
            <input data-testid="ad-subtitle" placeholder={t("admin.ads.subtitle")} className={input} value={form.subtitle} onChange={set("subtitle")} />
            <input data-testid="ad-image" placeholder={t("admin.ads.imageUrl")} className={input} value={form.image_url} onChange={set("image_url")} />
            <input data-testid="ad-link" placeholder={t("admin.ads.linkUrl")} className={input} value={form.link_url} onChange={set("link_url")} />
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="ad-placement" className={input} value={form.placement} onChange={set("placement")}>
                <option value="home">home</option>
                <option value="feed">feed</option>
              </select>
              <input data-testid="ad-price" type="number" placeholder={t("admin.ads.price")} className={input} value={form.price} onChange={set("price")} />
            </div>
            <DialogFooter>
              <button type="submit" data-testid="ad-save" disabled={saving} className="px-5 py-2.5 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover disabled:opacity-60">{saving ? "..." : t("admin.ads.save")}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
